import React, { useState, useEffect } from 'react';
import {
  FaBars, FaTimes, FaGamepad, FaShareAlt,
  FaGithub, FaWhatsapp, FaLinkedin, FaEnvelope,
  FaInstagram, FaMedium, FaSpotify
} from 'react-icons/fa';
import { Sun, Moon } from 'lucide-react';
import ContactModal from './ContactModal';
import { useTheme } from '../context/ThemeContext';

const SOCIAL_LINKS = [
  { icon: <FaGithub className="text-2xl" />, name: "GitHub", url: "https://github.com/NakhalanAtqiyaAP", color: "bg-gray-800 hover:bg-gray-900" },
  { icon: <FaInstagram className="text-2xl" />, name: "Instagram", url: "https://www.instagram.com/orangejahad/", color: "bg-orange-500 hover:bg-orange-600" },
  { icon: <FaEnvelope className="text-2xl" />, name: "Email", url: "mailto:nakhalanatqiyaarifin@gmail.com", color: "bg-red-500 hover:bg-red-600" },
  { icon: <FaWhatsapp className="text-2xl" />, name: "WhatsApp", url: "https://wa.me/6285715383055", color: "bg-green-500 hover:bg-green-600" },
  { icon: <FaLinkedin className="text-2xl" />, name: "LinkedIn", url: "https://www.linkedin.com/in/nakhalan-atqiya-arifin-putra-4b5b082a3/", color: "bg-blue-700 hover:bg-blue-800" },
  { icon: <FaMedium className="text-2xl" />, name: "Medium", url: "https://medium.com/@nakhalanatqiya", color: "bg-gray-900 hover:bg-gray-800" },
  { icon: <FaSpotify className="text-2xl" />, name: "Spotify", url: "https://open.spotify.com", color: "bg-green-500 hover:bg-green-600" },
];

// Rule of Life berada di urutan kedua setelah Profil
const MENU_ITEMS = [
  { id: 'profile', label: 'Profil' },
  { id: 'rule-of-life', label: 'Rule of Life' },
  { id: 'experience', label: 'Pengalaman' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'certificates', label: 'Sertifikat' }
];

const Header = ({ activeSection, setActiveSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        // Izinkan penyesuaian saat resize
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMenuClick = (itemId) => {
    setIsMenuOpen(false);
    setActiveSection(itemId);
  };

  const headerBg = isDark
    ? 'bg-[#0D1520]/95 border-[#1E3A5F]'
    : 'bg-white/95 border-gray-200';

  const brandText = isDark ? 'text-blue-400' : 'text-gray-800';

  const navActive = isDark
    ? 'bg-blue-600 text-white'
    : 'bg-blue-600 text-white shadow-sm';

  const dropdownBg = isDark
    ? 'bg-[#0D1520] border-[#1E3A5F]'
    : 'bg-white border-gray-200';

  const menuInactive = isDark
    ? 'bg-[#111C2C] text-slate-300 hover:bg-blue-900 hover:text-blue-300'
    : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-600';

  const toggleBtnBg = isDark
    ? 'bg-[#111C2C] text-slate-300 hover:bg-blue-900'
    : 'bg-white text-gray-800 hover:bg-gray-100';

  return (
    <>
      <header className={`${headerBg} backdrop-blur-sm sticky top-0 z-50 border-b shadow-sm transition-all`}>
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          
          {/* Brand Logo & Title */}
          <button 
            onClick={() => setActiveSection('profile')}
            className="flex items-center space-x-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-sm text-left group"
            aria-label="Kembali ke profil Nakhalan Atqiya"
          >
            <div className="w-8 h-8 md:w-9 md:h-9 bg-blue-600 pixel-border flex items-center justify-center shrink-0 group-hover:bg-blue-700 transition-colors">
              <FaGamepad className="text-white text-base md:text-lg" />
            </div>
            <span className={`text-lg md:text-xl font-bold ${brandText} pixel-font inline-block`}>
              Nakhalan Atqiya
            </span>
          </button>

          {/* Action Control Group */}
          <div className="flex items-center gap-2 md:gap-3">
            
            {/* Tombol Terpisah: Media Sosial */}
            <button
              onClick={() => setIsSocialModalOpen(true)}
              aria-label="Buka Media Sosial"
              className={`px-3 py-1.5 flex items-center gap-2 pixel-button transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark 
                  ? 'bg-blue-600/20 text-blue-400 border-blue-500 hover:bg-blue-600/30' 
                  : 'bg-blue-50 text-blue-600 border-blue-300 hover:bg-blue-100'
              }`}
            >
              <FaShareAlt className="text-sm" />
              <span className="text-[10px] pixel-font uppercase hidden sm:inline-block">
                SOSIAL
              </span>
            </button>

            {/* Toggle Theme Switch (Teks menunjukkan mode tujuan / aksi berpindah) */}
            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`px-3 py-1.5 flex items-center gap-2 pixel-button transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-[#111C2C] text-yellow-300 border-[#1E3A5F] hover:bg-[#1A293D]'
                  : 'bg-slate-800 text-yellow-300 border-slate-700 hover:bg-slate-900'
              }`}
            >
              <div className="relative w-4 h-4 flex items-center justify-center">
                {isDark ? (
                  <Sun size={16} strokeWidth={2.5} className="text-yellow-300" />
                ) : (
                  <Moon size={16} strokeWidth={2.5} className="text-yellow-300" />
                )}
              </div>
              <span className="text-[10px] pixel-font uppercase tracking-wider hidden sm:inline-block">
                {isDark ? 'LIGHT' : 'DARK'}
              </span>
            </button>

            {/* Universal Hamburger Menu Button */}
            <button 
              type="button"
              className={`px-3 py-1.5 md:py-2 flex items-center gap-2 pixel-button focus:outline-none focus:ring-2 focus:ring-blue-500 ${toggleBtnBg}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? "Tutup menu navigasi" : "Buka menu navigasi"}
            >
              {isMenuOpen ? <FaTimes className="text-base md:text-lg" /> : <FaBars className="text-base md:text-lg" />}
              <span className="text-xs pixel-font uppercase hidden md:inline-block">
                MENU
              </span>
            </button>

          </div>
        </div>

        {/* Dropdown Navigation Menu */}
        {isMenuOpen && (
          <nav 
            aria-label="Navigasi Utama" 
            className={`absolute top-full right-0 left-0 md:left-auto md:right-4 md:w-72 ${dropdownBg} pixel-border border-t md:border-t-2 shadow-xl transition-all z-50`}
          >
            <ul className="container mx-auto md:w-full px-4 py-3 md:p-3 space-y-2">
              {MENU_ITEMS.map((item) => {
                const isActive = activeSection === item.id;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleMenuClick(item.id)}
                      aria-current={isActive ? 'page' : undefined}
                      className={`w-full text-left px-4 py-2.5 text-sm font-medium transition-all duration-200 pixel-button focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${
                        isActive ? navActive : menuInactive
                      }`}
                    >
                      <span>{item.label}</span>
                      {isActive && <span className="text-xs pixel-font">◄</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </header>

      {/* Contact Modal */}
      <ContactModal
        isOpen={isSocialModalOpen}
        onClose={() => setIsSocialModalOpen(false)}
        socialLinks={SOCIAL_LINKS}
      />
    </>
  );
};

export default Header;